import React from "react";
import { Link } from "react-router-dom";

type AuthLayoutProps = {
    title: string;
    description: string;
    ctaLabel?: string;
    ctaHref?: string;
    ctaPrompt?: string;
    children: React.ReactNode;
    accent?: "emerald" | "sky" | "red";
    beforeCard?: React.ReactNode;
};

const AuthLayout: React.FC<AuthLayoutProps> = ({
    title,
    description,
    ctaLabel,
    ctaHref,
    ctaPrompt,
    children,
    accent = "emerald",
    beforeCard,
}) => {
    const accentRing =
        accent === "sky"
            ? "ring-sky-500/60 shadow-sky-500/30 from-sky-400 via-blue-500 to-indigo-500"
            : accent === "red"
                ? "ring-red-500/60 shadow-red-500/30 from-red-400 via-rose-500 to-pink-500"
                : "ring-emerald-500/60 shadow-emerald-500/30 from-emerald-400 via-teal-500 to-cyan-500";

    const ctaLinkClasses =
        accent === "sky"
            ? "font-medium text-sky-300 transition hover:text-sky-200"
            : accent === "red"
                ? "font-medium text-red-300 transition hover:text-red-200"
                : "font-medium text-emerald-300 transition hover:text-emerald-200";

    return (
        <div className="relative min-h-screen bg-slate-950 text-slate-100">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-60 top-10 h-96 w-96 rounded-full bg-sky-600/20 blur-3xl" />
                <div className="absolute -right-40 bottom-0 h-[32rem] w-[32rem] rounded-full bg-emerald-500/10 blur-[160px]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.15),_transparent_50%)]" />
            </div>

            <div className="relative flex min-h-screen items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="mb-10 inline-flex items-center gap-2">
                        <span
                            className={`relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${accentRing} text-xl font-semibold text-slate-950 shadow-2xl`}
                        >
                            <span className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/60" />
                            SL
                        </span>
                        <div className="leading-tight">
                            <span className="block text-xs uppercase tracking-[0.4em] text-slate-300">
                                SIG
                            </span>
                            <span className="text-lg font-semibold text-white">LLM</span>
                        </div>
                    </Link>

                    {beforeCard}

                    <div className="space-y-6 rounded-2xl border border-white/10 bg-slate-900/60 p-8 backdrop-blur">
                        <div className="space-y-4">
                            <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                                {title}
                            </h1>
                            <p className="text-base text-slate-300">{description}</p>
                        </div>

                        <div className="rounded-2xl border border-white/5 bg-slate-950/60">
                            {ctaHref && ctaLabel && ctaPrompt && (
                                <div className="flex items-center justify-between border-b border-white/5 px-6 py-4 text-sm">
                                    <span className="text-slate-300">{ctaPrompt}</span>
                                    <Link to={ctaHref} className={ctaLinkClasses}>
                                        {ctaLabel}
                                    </Link>
                                </div>
                            )}
                            <div className="px-6 py-6">{children}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthLayout;
